import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { Box } from '@mui/material';
import { pageSurface, contentColumn } from '../../styles/visualLanguage';
import type { RootState } from '../../redux/rootReducer';
import { AdvisoryIntakeScreen } from './components/AdvisoryIntakeScreen';
import { normalizeControlCharacters } from '../../redux/slices/advisory/advisoryTextSanitize';
import { isUnifiedReviewEnrollmentEnabled } from '../../hooks/advisory/advisoryReview';
import type { EngagementEngine } from '../../hooks/advisory/advisoryEngine';

/**
 * AdvisoryIntakePage — the `/advisory/new` route.
 *
 * Auth guard: duplicates `AdvisoryListPage.tsx`'s inline redirect pattern
 * verbatim — read, never modified, no shared guard extracted.
 *
 * This page registers no listener and reads no advisory slice state: no
 * engagement exists yet at this route. It deliberately does NOT call the
 * supervisor write-path hook (`hooks/advisory`) that fires the turn. That
 * hook's unmount effect aborts whatever fetch is in flight
 * unconditionally, and this page unmounts the instant it navigates away on
 * submit — if this page owned the turn, its own navigate would abort its
 * own POST, and agent-svc writes the goal document *before* the LLM call,
 * so an abort in that window could cancel the write the whole flow depends
 * on. The component instance that calls the hook must be the same
 * instance that fires the turn, for the lifetime of that turn, so
 * `AdvisoryEngagementPage` owns the hook instead and fires the turn on
 * arrival, using the question this page carries forward in router
 * location state.
 */
export function AdvisoryIntakePage() {
  const navigate = useNavigate();
  const { isAuthenticated, loading: userLoading } = useSelector((state: RootState) => state.user);
  const [question, setQuestion] = useState('');
  const [engine, setEngine] = useState<EngagementEngine>('astra');
  const enrollReview = isUnifiedReviewEnrollmentEnabled();
  const submitted = useRef(false);

  // Redirecting during auth bootstrap would bounce a signed-in user off
  // their own page, so this waits for `userLoading` to finish.
  useEffect(() => {
    if (!userLoading && !isAuthenticated) {
      navigate('/signin');
    }
  }, [isAuthenticated, userLoading, navigate]);

  // No path has been chosen yet at this route, so the counter shows
  // position only, no denominator.

  const handleSubmit = () => {
    // Sanitize before the emptiness check, not after — a value that
    // consists only of control characters must not navigate, even though
    // a plain `.trim()` would not have caught it (control characters are
    // not whitespace).
    const sanitized = normalizeControlCharacters(question);
    if (!sanitized || submitted.current) {
      return;
    }
    // Minted at submit, never at mount or module scope, so every
    // submission is a distinct engagement.
    submitted.current = true;
    const freshEngagementId = uuidv4();
    // The destination is a template over a locally generated uuid and
    // nothing else — no segment comes from the URL, the query string or
    // any document. `replace: true` keeps this screen out of the back
    // stack.
    navigate(`/advisory/${freshEngagementId}`, {
      replace: true,
      state: { userQuery: sanitized, ...(enrollReview ? { engine } : {}) },
    });
  };

  return (
    <Box
      data-testid="advisory-intake-frame"
      sx={[
        pageSurface,
        { display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2.5 },
      ]}
    >
      <Box sx={[contentColumn, { minWidth: 0 }]}>
        <AdvisoryIntakeScreen
          question={question}
          onQuestionChange={setQuestion}
          onSubmit={handleSubmit}
          engine={enrollReview ? engine : undefined}
          onEngineChange={enrollReview ? setEngine : undefined}
        />
      </Box>
    </Box>
  );
}

export default AdvisoryIntakePage;
